'use client';
import { useState } from 'react';
import { Check } from 'lucide-react';
import { LabelMark } from '../../components/ui/StatusMark';
import { useTranslation } from '../../lib/i18n';
import { useBrainSessionStatus } from '../../lib/queries';
import type { BrainModelOption } from '../../lib/types';
import { brainModelProviderGroups } from '../../components/ui/brainModelSelection';
import { SOURCE_BADGE } from '../../lib/modelProvider';
import { ModelIcon } from '../../components/ui/ModelIcon';
import { ReasoningScale } from '../../components/ui/ReasoningScale';
import {
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '../../components/ui/shadcn/dropdown-menu';
import { choiceCheckClass, choiceItemClass } from '../../components/ui/shadcn/select';
import { cn } from '../../lib/utils';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';

function modelValue(model: Pick<BrainModelOption, 'provider' | 'model'>): string {
  return JSON.stringify([model.provider, model.model]);
}

/** The same row as every single-choice dropdown: brand mark and name, the running model trailed by the
 *  shared check. Its wash comes from the radio item's `data-state="checked"` through `choiceItemClass`. */
function ModelOptionRow({ model, active, activeLabel }: {
  model: BrainModelOption; active: boolean; activeLabel: string;
}) {
  return (
    <>
      <ModelIcon name={model.model} size={14} />
      <span className="min-w-0 flex-1 truncate">{model.model}</span>
      {active ? <Check size={15} className={choiceCheckClass} aria-label={activeLabel} /> : null}
    </>
  );
}

/** The catalog rows of the model picker menu. Only the expanded row shows reasoning;
 * selecting another model is a preview until its level or a second row press confirms it. */
export function ModelOptionList({
  onPickAction,
}: {
  onPickAction?: () => void;
}) {
  const { t } = useTranslation();
  const { models, currentModel, provider, setModel, modelsLoading, modelsError, loadModels, activeSessionId, reasoning } = useBrainChat();
  const { childFocus } = useBrainChatStatus();
  const statusQuery = useBrainSessionStatus(activeSessionId, !!models?.some((model) => model.reasoningLevels?.length));
  const { applied, status: saveStatus, apply } = reasoning;
  const [selected, setSelected] = useState<string | null>(null);

  const groups = brainModelProviderGroups(models ?? []);

  if (modelsLoading) {
    return <DropdownMenuItem disabled className="text-small italic text-muted-foreground">{t.brainChat.modelPickerLoading}</DropdownMenuItem>;
  }
  if (modelsError) {
    return (
      <div className="flex flex-col gap-1.5 px-3 py-2 text-small text-muted-foreground">
        <span>{t.brainChat.modelPickerError}</span>
        <DropdownMenuItem
          onSelect={(event) => { event.preventDefault(); loadModels(); }}
          className="w-auto self-start rounded-md border border-border px-2 py-0.5 text-small text-foreground"
        >{t.brainChat.modelPickerRetry}</DropdownMenuItem>
      </div>
    );
  }
  if (groups.length === 0) {
    return <DropdownMenuItem disabled className="text-small italic text-muted-foreground">{t.brainChat.modelPickerEmpty}</DropdownMenuItem>;
  }

  const pick = (model: BrainModelOption): void => {
    if (modelValue(model) !== modelValue({ provider, model: currentModel })) void setModel(model);
    onPickAction?.();
  };
  const select = (model: BrainModelOption): boolean => {
    if (model.reasoningLevels?.length && modelValue(model) !== currentValue && modelValue(model) !== (selected ?? currentValue)) {
      setSelected(modelValue(model));
      return false;
    }
    pick(model);
    return true;
  };
  const currentValue = modelValue({ provider, model: currentModel });
  const content = groups.map((group) => (
    <div key={group.id} className="py-0.5">
      <div className="flex items-center gap-1.5 px-2.5 py-1 uppercase text-eyebrow text-muted-foreground">
        <ModelIcon name={group.label} size={14} />
        <span className="truncate">{group.label}</span>
        <span className="normal-case"><LabelMark tone="muted">{SOURCE_BADGE[group.models[0]!.source]}</LabelMark></span>
      </div>
      {group.models.map((model) => {
        const active = model.provider === provider && model.model === currentModel;
        const levels = model.reasoningLevels ?? [];
        const expanded = (selected ?? currentValue) === modelValue(model) && levels.length > 0;
        // Only the running model has a known level. Another model's level after a switch is decided by the
        // daemon (restored transcript state, account default, clamped to that model), so its scale shows no
        // stop rather than a guess; picking one switches first and then applies it.
        const current = active ? applied ?? statusQuery.data?.thinkingLevel ?? '' : '';
        return (
          <div key={modelValue(model)}>
            <DropdownMenuRadioItem
              value={modelValue(model)}
              aria-expanded={expanded}
              onSelect={(event) => { if (!select(model)) event.preventDefault(); }}
              // The radio indicator is hidden: the shared trailing check marks the running model instead.
              className={cn(choiceItemClass, '[&>span:first-child]:hidden', expanded && !active && 'bg-accent text-accent-foreground')}
            >
              <ModelOptionRow model={model} active={active} activeLabel={t.brainChat.modelActive} />
            </DropdownMenuRadioItem>
            {expanded ? (
              <div className="min-w-0 pb-2 pl-8 pr-2.5"
                title={childFocus ? t.brainChat.childFocusCommandDisabled : undefined}
                onKeyDown={(event) => { if (event.key.startsWith('Arrow') || event.key === 'Home' || event.key === 'End') event.stopPropagation(); }}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <ReasoningScale
                  options={levels.map((level) => ({ value: level, label: model.reasoningLabels?.[level] ?? level }))}
                  value={current}
                  onChange={(level) => { void apply(level, current, active ? undefined : () => setModel(model)).then(() => onPickAction?.()); }}
                  ariaLabel={t.brainChat.modelReasoning}
                  disabled={!!childFocus || saveStatus === 'saving' || saveStatus === 'unconfirmed' || !statusQuery.data}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  ));

  return <DropdownMenuRadioGroup value={currentValue}>{content}</DropdownMenuRadioGroup>;
}
